import { Schedule } from '../../types';
import { CalendarItem } from '../calendar-item';

// Shared month-grouped renderer for calendar lessons. Used by both the
// schedule drawer (features/calendar/calendar.tsx) and the standalone
// Parent Connect / PTM page so the row visuals, status logic, and
// `data-calendar-index` (drawer scroll anchor) stay identical.

const getDisplayStatus = (
  lesson: Schedule.Lesson,
  globalIndex: number,
  firstUpcomingIndex: number
) => {
  const isFirstUpcoming =
    lesson.status === 'upcoming' && globalIndex === firstUpcomingIndex;
  const shouldShowStatus = lesson.status !== 'upcoming' || isFirstUpcoming;

  let displayStatus: Schedule.Lesson['status'];

  if (isFirstUpcoming) {
    displayStatus = 'upcoming';
  } else if (lesson.status === 'upcoming') {
    displayStatus = undefined;
  } else if (lesson.status === 'holiday') {
    displayStatus = 'holiday';
  } else {
    displayStatus = lesson.status;
  }

  return { displayStatus, shouldShowStatus };
};

const stripYearFromDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
};

const monthYearOf = (lesson: Schedule.Lesson): string =>
  new Date(lesson.date).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

// Lessons are expected pre-sorted ascending by the caller, so inserting
// in order keeps the month sections in chronological order.
const groupByMonth = (
  lessons: Schedule.Lesson[]
): Array<[string, Schedule.Lesson[]]> => {
  const grouped = new Map<string, Schedule.Lesson[]>();
  for (const lesson of lessons) {
    const key = monthYearOf(lesson);
    const bucket = grouped.get(key);
    if (bucket) {
      bucket.push(lesson);
    } else {
      grouped.set(key, [lesson]);
    }
  }
  return [...grouped.entries()];
};

const renderMonthLessons = (
  monthLessons: Schedule.Lesson[],
  sortedLessons: Schedule.Lesson[],
  firstUpcomingIndex: number
) => {
  return monthLessons.map(lesson => {
    const globalIndex = sortedLessons.findIndex(
      l =>
        l.name === lesson.name &&
        l.date === lesson.date &&
        l.time === lesson.time
    );

    const { displayStatus, shouldShowStatus } = getDisplayStatus(
      lesson,
      globalIndex,
      firstUpcomingIndex
    );

    return (
      <CalendarItem
        key={`${lesson.name}-${lesson.date}-${globalIndex}`}
        data-calendar-index={globalIndex}
        title={lesson.name}
        date={stripYearFromDate(lesson.date)}
        day={lesson.day}
        time={lesson.time ?? undefined}
        status={displayStatus}
        variant={lesson.variant}
        start_time={lesson.start_time ?? 0}
        joiningLink={lesson.joiningLink ?? ''}
        shouldShowStatus={shouldShowStatus}
        pcState={lesson.pcState}
        pcSessionId={lesson.pcSessionId}
        pcRespondHref={lesson.pcRespondHref}
        pcCancelRescheduleHref={lesson.pcCancelRescheduleHref}
        pcEditRequestHref={lesson.pcEditRequestHref}
        pcRescheduleAttemptsLeft={lesson.pcRescheduleAttemptsLeft}
      />
    );
  });
};

interface LessonsListProps {
  // Pre-sorted (ascending) lessons to render.
  lessons: Schedule.Lesson[];
  // 'Select all' (default) shows every month; otherwise only the matching
  // "Month YYYY" section is rendered.
  selectedMonth?: string;
}

export const LessonsList = ({
  lessons,
  selectedMonth = 'Select all',
}: LessonsListProps) => {
  const grouped = groupByMonth(lessons);
  const visible =
    selectedMonth === 'Select all'
      ? grouped
      : grouped.filter(([monthYear]) => monthYear === selectedMonth);
  const firstUpcomingIndex = lessons.findIndex(l => l.status === 'upcoming');

  return (
    <>
      {visible.map(([monthYear, monthLessons]) => (
        <div key={monthYear} className="mb-8">
          <div className="sticky top-0 bg-white mb-4 py-2 -mx-4 px-4 z-10">
            <h2 className="text-md lg:text-lg font-semibold text-magical-moonlight-d2">
              {monthYear}
            </h2>
          </div>

          <div className="space-y-3">
            {renderMonthLessons(monthLessons, lessons, firstUpcomingIndex)}
          </div>
        </div>
      ))}
    </>
  );
};
